//#region ../src/utils/toast-core.ts
/**
* A transient message, announced to assistive technology, in any container.
*
* WHY THE FRAMEWORK OWNS THIS. There were four toasts in the workspace, and the
* three outside this file agreed on nothing: one announced politely, one had no
* ARIA at all, one wrote its text synchronously, one deferred it, two attached
* and detached the element while two toggled a class. The mechanism below
* carries corrections that cost three review rounds between them and are
* invisible in the finished code — which is exactly the kind of thing a second
* hand-written copy reproduces the bugs of rather than the fixes.
*
* WHAT IT DOES NOT DO. It does not own the toast's PLACEMENT or its LIFETIME.
* A container-scoped factory and a document-level singleton are genuinely
* different lifetimes, and an immersive-AR overlay is a genuinely different
* placement from a page. Callers keep those; this owns the element, the ARIA
* contract, the timer and the replace-and-restart semantics.
*
* @see toast-core.ts.md
*/
/** How long a message stays before it goes, ms. */
const DEFAULT_TOAST_LINGER_MS = 6e3;
/**
* Creates a toast surface inside `root`.
*
* THE TWO CORRECTIONS THIS CARRIES, both invisible in the code:
*
* 1. **The text is written in the NEXT task, not the same one.** A live region
*    is announced when its content changes while it is in the accessibility
*    tree; one inserted already carrying its text is commonly not announced at
*    all. Reordering the two statements reads correctly and does nothing —
*    browsers flush accessibility updates once at the end of a task, so the AT
*    still sees a region that appeared fully populated. The separation has to
*    be a task boundary.
*    - `setTimeout`, not `requestAnimationFrame`: rAF is the tighter fit for
*      "after a rendering step" but is throttled or paused in a background tab,
*      and messages are emitted with no rendering guaranteed. A task always
*      fires.
* 2. **Withdrawal and supersession are handled by CANCELLING the timer, never
*    by guarding inside the callback.** Guards on `isConnected` or a sequence
*    number can never be false: both `clear()` and a second `show()` cancel the
*    pending write before anything else, so a superseded write never runs. The
*    guards were deleted because the sidecar had begun describing them as the
*    mechanism — a description asserting something the code does not do, which
*    is the same defect this component exists to fix, one level up.
*
* **The element is attached on `show` and removed on `clear`**, rather than
* living in the DOM permanently. For an AR overlay root that is mandatory: such
* a root is typically `position: fixed; inset: 0` and hidden only while
* `:empty`, so a permanent child would keep a full-viewport click-eating layer
* over the page whenever AR is NOT running — a regression already recorded in
* the OSM demo's `ar-mode.ts`. For an ordinary page root it is merely tidy, and
* one rule for both is worth more than the saved DOM operation.
*/
function createToast(root, options = {}) {
	const element = document.createElement("div");
	const defaultClassName = options.className ?? "toast";
	element.className = defaultClassName;
	if (options.id !== void 0) element.id = options.id;
	element.setAttribute("role", "status");
	element.setAttribute("aria-live", "polite");
	const defaultLingerMs = options.lingerMs ?? 6e3;
	let timer;
	/** The deferred text write. See the function comment. */
	let pending;
	const clear = () => {
		if (timer !== void 0) {
			clearTimeout(timer);
			timer = void 0;
		}
		if (pending !== void 0) {
			clearTimeout(pending);
			pending = void 0;
		}
		element.textContent = "";
		element.remove();
	};
	return {
		show(message, showOptions = {}) {
			if (pending !== void 0) clearTimeout(pending);
			element.textContent = "";
			element.className = showOptions.className ?? defaultClassName;
			if (element.parentNode !== root) root.append(element);
			pending = setTimeout(() => {
				pending = void 0;
				element.textContent = message;
			}, 0);
			if (timer !== void 0) clearTimeout(timer);
			timer = setTimeout(clear, showOptions.lingerMs ?? defaultLingerMs);
		},
		clear
	};
}
//#endregion
export { DEFAULT_TOAST_LINGER_MS, createToast };
